import React, { useEffect, useRef, useState } from 'react';

import {
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  MoreHorizontal,
  Clock,
  Hash,
  Calendar,
  Trash2,
  Loader2,
  Check,
  Wand2,
  Upload,
  Image as ImageIcon,
  ChevronRight,
  CalendarClock,
  Save,
  RefreshCw,
  Copy,
  MessageSquare,
  AlertTriangle,
  X,
  Video,
  Filter,
} from 'lucide-react';
import { BrandVoiceProfile, GeneratedWeek, ScheduledPost, weekDisplayLabel } from '@/lib/brandStudio';
import { activeBrand } from '@/lib/adminBrand';


type Step = 1 | 2 | 3 | 4 | 5 | 6;
interface PostEditorProps {
  posts: ScheduledPost[];
  weeks: GeneratedWeek[];
  /** When set, the editor opens filtered to ONLY this week (the freshly generated one). */
  defaultWeekId?: string | null;
  voice: BrandVoiceProfile | null;
  onSave: (id: string, patch: Partial<ScheduledPost>) => Promise<void>;
  onSubmitForApproval: (p: ScheduledPost) => Promise<void>;
  onDelete: (id: string) => Promise<void> | void;
  onDeleteWeek: (id: string) => Promise<void>;
  onGenerateImage: (p: ScheduledPost) => Promise<void>;
  onRegeneratePost: (p: ScheduledPost) => Promise<Partial<ScheduledPost>>;
  onUploadImage: (file: File) => Promise<string | null>;
  fmtDateTime: (iso: string | null) => string;
  goToStep: (s: Step) => void;
}

export const typeColors: Record<string, string> = {
  'Feed Post': 'bg-[#C9A961]/15 text-[#C9A961] border-[#C9A961]/30',
  Reel: 'bg-[#1A3B5C]/10 text-[#1A3B5C] border-[#1A3B5C]/30',
  Carousel: 'bg-[#8B6F47]/15 text-[#8B6F47] border-[#8B6F47]/30',
  Story: 'bg-rose-100 text-rose-700 border-rose-300',
};

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600 border-slate-300',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  scheduled: 'bg-[#C9A961]/15 text-[#8B6F47] border-[#C9A961]/40',
  published: 'bg-blue-50 text-blue-700 border-blue-300',
  failed: 'bg-red-50 text-red-700 border-red-300',
};

/**
 * Touch-safe delete button with an INLINE two-tap confirmation.
 *
 * Why: the previous flow relied on window.confirm(), which many mobile
 * browsers (iOS standalone / in-app webviews) silently block — so taps on
 * "Delete" appeared to do nothing on phones. This component never uses
 * native dialogs: the first tap arms it ("Tap to confirm"), the second tap
 * deletes. It auto-disarms after 4 seconds or via the ✕.
 */
const ConfirmDeleteButton: React.FC<{
  onConfirm: () => Promise<void> | void;
  label?: string;
  confirmLabel?: string;
  compact?: boolean;
  className?: string;
}> = ({ onConfirm, label = 'Delete', confirmLabel = 'Tap to confirm', compact = false, className = '' }) => {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const arm = () => {
    setArmed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setArmed(false), 4000);
  };

  const fire = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
      setArmed(false);
    }
  };

  if (busy) {
    return (
      <span
        className={`inline-flex items-center gap-2 font-sans text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 border border-red-200 bg-red-50 text-red-500 ${className}`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Deleting…
      </span>
    );
  }

  if (armed) {
    return (
      <span className={`inline-flex items-stretch ${className}`}>
        <button
          type="button"
          onClick={fire}
          className="font-sans text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 bg-red-600 text-white hover:bg-red-700 active:bg-red-700 flex items-center gap-2"
        >
          <Trash2 className="w-3.5 h-3.5" /> {confirmLabel}
        </button>
        <button
          type="button"
          onClick={() => {
            if (timerRef.current) clearTimeout(timerRef.current);
            setArmed(false);
          }}
          aria-label="Cancel delete"
          className="px-2.5 border border-l-0 border-red-200 bg-white text-slate-400 hover:text-slate-600 flex items-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={arm}
      className={`font-sans text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500 active:border-red-300 active:text-red-500 flex items-center gap-2 ${className}`}
    >
      <Trash2 className="w-3.5 h-3.5" /> {!compact && label}
      {compact && <span className="sr-only">{label}</span>}
    </button>
  );
};

const PostEditor: React.FC<PostEditorProps> = ({
  posts,
  weeks,
  defaultWeekId = null,
  voice,
  onSave,
  onSubmitForApproval,
  onDelete,
  onDeleteWeek,
  onGenerateImage,
  onRegeneratePost,
  onUploadImage,
  fmtDateTime,
  goToStep,
}) => {
  // Default the week filter to the freshly generated week so Step 3 shows
  // exactly its 7 posts (and never mixes in older runs as "duplicates").
  const [filterWeek, setFilterWeek] = useState<string>(defaultWeekId || 'all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (defaultWeekId) setFilterWeek(defaultWeekId);
  }, [defaultWeekId]);

  if (posts.length === 0) {
    return (
      <div className="text-center py-16">
        <CalendarClock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <h3 className="font-serif text-xl text-[#0D1B2A]">No Posts to Edit Yet</h3>
        <p className="font-sans text-sm text-slate-500 mt-2">Generate a week in Step 2 to populate the editor.</p>
        <button
          onClick={() => goToStep(2)}
          className="mt-5 font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]"
        >
          Go to Step 2
        </button>
      </div>
    );
  }

  const filtered = posts.filter((p) => {
    const ap = p.approval_status || 'draft';
    if (ap === 'pending' || ap === 'approved') return false; // only draft + sent-back live here
    if (filterWeek !== 'all' && p.week_id !== filterWeek) return false;
    if (filterStatus !== 'all' && (p.status || 'draft') !== filterStatus) return false;
    return true;
  });

  const selectedWeek = filterWeek !== 'all' ? weeks.find((w) => w.id === filterWeek) || null : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 3 · Post Editor</h3>
          <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
            Each post is shown exactly as it will appear on Instagram. Edit the picture, caption, hashtags,
            call-to-action, post type, and schedule — then click <strong>Send to Admin for Approval</strong>.
            Posts the admin sends back reappear here with feedback.
          </p>
        </div>
        <button
          onClick={() => goToStep(4)}
          className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] transition-colors flex items-center gap-2"
        >
          Admin Approval <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Toolbar — filters + week management, stacks cleanly on mobile */}
      <div className="border border-slate-200 bg-[#FAF8F3]/60 p-4 space-y-3">
        <div className="flex items-center gap-2 font-sans text-[10px] tracking-[0.25em] uppercase text-slate-400">
          <Filter className="w-3.5 h-3.5" /> Filters
          <span className="ml-auto normal-case tracking-normal text-xs text-slate-500">
            Showing {filtered.length} of {posts.length} posts
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-end gap-3">
          <div className="lg:w-64">
            <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-1.5">
              Week
            </label>
            <select
              value={filterWeek}
              onChange={(e) => setFilterWeek(e.target.value)}
              className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A]"
            >
              <option value="all">All Weeks</option>
              {weeks.map((w) => (
                <option key={w.id} value={w.id}>
                  {weekDisplayLabel(w, posts)}
                </option>
              ))}
            </select>
          </div>
          <div className="lg:w-48">
            <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-1.5">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A]"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
            </select>
          </div>
          {selectedWeek && (
            <div className="sm:col-span-2 lg:col-auto lg:ml-auto">
              <ConfirmDeleteButton
                label={`Delete Week ${weekDisplayLabel(selectedWeek, posts)}`}
                confirmLabel="Confirm — delete week & posts"
                onConfirm={async () => {
                  await onDeleteWeek(selectedWeek.id);
                  setFilterWeek('all');
                }}
                className="w-full lg:w-auto justify-center"
              />
            </div>
          )}
        </div>
        {selectedWeek && (
          <p className="font-sans text-[11px] text-slate-400">
            Deleting a week removes the week and every post inside it. Select a week above to manage it.
          </p>
        )}
      </div>

      {/* Post cards */}
      <div className="space-y-8">
        {filtered.map((post, index) => (
          <PostEditorRow
            key={post.id}
            post={post}
            index={index}
            total={filtered.length}
            voice={voice}
            onSave={onSave}
            onApprove={onSubmitForApproval}
            onDelete={onDelete}
            onGenerateImage={onGenerateImage}
            onRegeneratePost={onRegeneratePost}
            onUploadImage={onUploadImage}
            fmtDateTime={fmtDateTime}
          />
        ))}
        {filtered.length === 0 && (
          <div className="border border-dashed border-slate-200 py-14 text-center bg-slate-50">
            <CalendarClock className="w-8 h-8 mx-auto text-slate-300 mb-3" />
            <p className="font-sans text-sm text-slate-400">No posts match the current filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

interface RowProps {
  post: ScheduledPost;
  index: number;
  total: number;
  voice: BrandVoiceProfile | null;
  onSave: (id: string, patch: Partial<ScheduledPost>) => Promise<void>;
  onApprove: (p: ScheduledPost) => Promise<void>;
  onDelete: (id: string) => Promise<void> | void;
  onGenerateImage: (p: ScheduledPost) => Promise<void>;
  onRegeneratePost: (p: ScheduledPost) => Promise<Partial<ScheduledPost>>;
  onUploadImage: (file: File) => Promise<string | null>;
  fmtDateTime: (iso: string | null) => string;
}

const PostEditorRow: React.FC<RowProps> = ({
  post,
  index,
  total,
  onSave,
  onApprove,
  onDelete,
  onGenerateImage,
  onRegeneratePost,
  onUploadImage,
  fmtDateTime,
}) => {
  // Local working draft so the IG mockup updates live as you type.
  const [draft, setDraft] = useState<ScheduledPost>(post);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [approving, setApproving] = useState(false);
  const [genImage, setGenImage] = useState(false);
  const [regening, setRegening] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // CRITICAL: When an image is generated for this post by an EXTERNAL flow
  // (e.g. the batch "Generate Images for This Week" button in Step 3, which
  // saves the image to the DB and then re-fetches all posts), the new image_url
  // arrives via the `post` prop. The local `draft` was seeded once with
  // useState(post) and would otherwise never pick up that change — so the
  // freshly-painted image was saved correctly but never appeared on its post.
  // This effect syncs the media fields from the incoming post into the draft,
  // while preserving any unsaved text edits (caption / CTA / hashtags / etc.).
  useEffect(() => {
    setDraft((d) => {
      if (
        d.image_url === post.image_url &&
        d.video_url === post.video_url &&
        d.image_prompt === post.image_prompt
      ) {
        return d;
      }
      return {
        ...d,
        image_url: post.image_url,
        video_url: post.video_url,
        image_prompt: post.image_prompt,
      };
    });
  }, [post.image_url, post.video_url, post.image_prompt]);

  // Fully recreate this day's post (caption + hashtags + CTA + theme + image).
  const recreate = async () => {
    setRegening(true);
    setErr(null);
    try {
      const patch = await onRegeneratePost(post);
      setDraft((d) => ({ ...d, ...patch }));
      setDirty(false);
    } catch (e: any) {
      setErr(e?.message || 'Could not recreate this post.');
    } finally {
      setRegening(false);
    }
  };

  // Copy the AI image prompt to the clipboard.
  const copyPrompt = async () => {
    const text = draft.image_prompt || '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 1600);
    } catch {
      setErr('Could not copy the prompt to your clipboard.');
    }
  };

  const update = (patch: Partial<ScheduledPost>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const buildPatch = (): Partial<ScheduledPost> => ({
    caption: draft.caption,
    hashtags: draft.hashtags,
    cta: draft.cta,
    post_type: draft.post_type,
    theme: draft.theme,
    day_label: draft.day_label,
    scheduled_for: draft.scheduled_for,
    image_url: draft.image_url,
    image_prompt: draft.image_prompt,
    video_url: draft.video_url,
  });

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onSave(post.id, buildPatch());
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (e: any) {
      setErr(e?.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    setApproving(true);
    setErr(null);
    try {
      if (dirty) await onSave(post.id, buildPatch());
      await onApprove({ ...post, ...buildPatch() });
      setDirty(false);
    } catch (e: any) {
      setErr(e?.message || 'Could not approve post.');
    } finally {
      setApproving(false);
    }
  };

  const generate = async () => {
    setGenImage(true);
    setErr(null);
    try {
      await onGenerateImage(post);
    } catch (e: any) {
      setErr(e?.message || 'Image generation failed.');
    } finally {
      setGenImage(false);
    }
  };

  const handleFile = async (file: File | null, kind: 'image' | 'video') => {
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const url = await onUploadImage(file);
      if (!url) throw new Error('Upload failed.');
      if (kind === 'video') {
        update({ video_url: url });
      } else {
        update({ image_url: url });
      }
    } catch (e: any) {
      setErr(e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const captionFirstLine = (draft.caption || '').split('\n')[0];
  const mediaBusy = genImage || uploading || regening;

  const mediaBtn =
    'font-sans text-[9px] tracking-[0.12em] uppercase px-3 py-2.5 flex items-center justify-center gap-1.5 border transition-colors disabled:opacity-50';

  return (
    <div className="border border-slate-200 bg-white shadow-sm shadow-[#1A3B5C]/5 overflow-hidden">
      {/* Card header — always-visible actions, easy touch targets */}
      <div className="flex items-center gap-3 flex-wrap px-4 sm:px-5 py-3 bg-[#FAF8F3] border-b border-[#C9A961]/25">
        <div className="w-7 h-7 rounded-full bg-[#0D1B2A] text-[#C9A961] font-serif text-sm flex items-center justify-center flex-shrink-0">
          {index + 1}
        </div>
        <div className="min-w-0">
          <div className="font-serif text-lg text-[#1A3B5C] leading-tight truncate">
            {draft.day_label || 'Post'}
            <span className="font-sans text-[10px] text-slate-400 ml-2 tracking-wider uppercase">
              {index + 1} of {total}
            </span>
          </div>
          <div className="font-sans text-[11px] text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {fmtDateTime(draft.scheduled_for)}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <span
            className={`px-3 py-1 border text-[10px] tracking-[0.15em] uppercase font-sans rounded-full ${
              statusColors[draft.status] || statusColors.draft
            }`}
          >
            {draft.status}
          </span>
          <span
            className={`hidden sm:inline-flex px-3 py-1 border text-[10px] tracking-[0.15em] uppercase font-sans ${
              typeColors[draft.post_type] || typeColors['Feed Post']
            }`}
          >
            {draft.post_type}
          </span>
          <ConfirmDeleteButton
            label="Delete"
            confirmLabel="Confirm delete"
            onConfirm={() => onDelete(post.id)}
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start p-4 sm:p-6">
        {/* Instagram live mockup + media toolbar */}
        <div className="lg:sticky lg:top-28">
          <div className="bg-white rounded-2xl shadow-xl shadow-[#1A3B5C]/10 overflow-hidden border border-[#C9A961]/20 max-w-[460px] mx-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                {activeBrand().igAvatar ? (
                  <img
                    src={activeBrand().igAvatar}
                    alt={`@${activeBrand().igHandle} profile picture`}
                    className="w-9 h-9 rounded-full object-cover border border-gray-200"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8B6F47] flex items-center justify-center">
                    <span className="font-serif text-white text-sm tracking-wider uppercase">{activeBrand().igHandle.charAt(0)}</span>
                  </div>
                )}
                <div>
                  <div className="font-sans text-[13px] font-semibold text-[#1A3B5C]">{activeBrand().igHandle}</div>
                  <div className="font-sans text-[10px] text-gray-500">{activeBrand().igLocation} · Sponsored</div>
                </div>

              </div>

              <MoreHorizontal className="w-5 h-5 text-gray-500" />
            </div>

            <div className="relative aspect-square bg-gray-100">
              {draft.video_url ? (
                <video src={draft.video_url} className="w-full h-full object-cover" muted loop playsInline />
              ) : draft.image_url ? (
                <img src={draft.image_url} alt={draft.theme || ''} className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 font-sans text-xs p-4 text-center">
                  <ImageIcon className="w-7 h-7 mb-2" />
                  {draft.image_prompt || 'No image yet'}
                </div>
              )}

              {/* Busy overlay — always visible while media work runs (no hover needed) */}
              {mediaBusy && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0D1B2A]/55">
                  <div className="flex flex-col items-center gap-2 bg-[#0D1B2A]/70 px-4 py-3 rounded">
                    <Loader2 className="w-5 h-5 text-[#C9A961] animate-spin" />
                    <span className="font-sans text-[9px] tracking-[0.15em] uppercase text-white">
                      {uploading ? 'Uploading…' : regening ? 'Recreating post…' : 'Painting…'}
                    </span>
                  </div>
                </div>
              )}

              {draft.post_type === 'Reel' && (
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-md px-2 py-1 text-white text-[10px] tracking-wider font-sans">
                  REEL
                </div>
              )}
              {draft.post_type === 'Carousel' && (
                <div className="absolute top-3 right-3 flex gap-1">
                  {[1, 2, 3].map((d) => (
                    <span key={d} className="w-1.5 h-1.5 rounded-full bg-white/80" />
                  ))}
                </div>
              )}
            </div>

            {/* Media toolbar — always visible so it works on touch devices too */}
            <div className="grid grid-cols-5 border-t border-gray-100 divide-x divide-gray-100">
              <label
                className={`${mediaBtn} border-0 bg-white text-[#0D1B2A] hover:bg-[#F3EFE6] cursor-pointer ${
                  mediaBusy ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                <span className="flex flex-col items-center gap-1">
                  <Upload className="w-4 h-4" /> Image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={mediaBusy}
                  onChange={(e) => handleFile(e.target.files?.[0] || null, 'image')}
                />
              </label>
              <label
                className={`${mediaBtn} border-0 bg-white text-[#0D1B2A] hover:bg-[#F3EFE6] cursor-pointer ${
                  mediaBusy ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                <span className="flex flex-col items-center gap-1">
                  <Video className="w-4 h-4" /> Video
                </span>
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  disabled={mediaBusy}
                  onChange={(e) => handleFile(e.target.files?.[0] || null, 'video')}
                />
              </label>
              <button onClick={generate} disabled={mediaBusy} className={`${mediaBtn} border-0 bg-white text-[#8B6F47] hover:bg-[#F3EFE6]`}>
                <span className="flex flex-col items-center gap-1">
                  <Wand2 className="w-4 h-4" /> AI Image
                </span>
              </button>
              <button onClick={recreate} disabled={mediaBusy} className={`${mediaBtn} border-0 bg-white text-[#1A3B5C] hover:bg-[#F3EFE6]`}>
                <span className="flex flex-col items-center gap-1">
                  <RefreshCw className="w-4 h-4" /> Recreate
                </span>
              </button>
              <button onClick={copyPrompt} disabled={mediaBusy} className={`${mediaBtn} border-0 bg-white text-[#0D1B2A] hover:bg-[#F3EFE6]`}>
                <span className="flex flex-col items-center gap-1">
                  {promptCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  {promptCopied ? 'Copied' : 'Prompt'}
                </span>
              </button>
            </div>

            <div className="px-4 pt-3 pb-2 flex items-center justify-between border-t border-gray-100">
              <div className="flex items-center gap-4">
                <Heart className="w-6 h-6 text-gray-800" />
                <MessageCircle className="w-6 h-6 text-gray-800" />
                <Send className="w-6 h-6 text-gray-800" />
              </div>
              <Bookmark className="w-6 h-6 text-gray-800" />
            </div>

            <div className="px-4 pb-4">
              <div className="font-sans text-[13px] font-semibold text-[#1A3B5C] mb-1">
                {(1200 + index * 247).toLocaleString()} likes
              </div>
              <div className="font-sans text-[13px] text-gray-800 leading-relaxed whitespace-pre-line">
                <span className="font-semibold text-[#1A3B5C]">{activeBrand().igHandle}</span> {captionFirstLine}

                {(draft.caption || '').includes('\n') && <button className="text-gray-500 ml-1">... more</button>}
              </div>
              <div className="font-sans text-[10px] text-gray-400 tracking-wider mt-2 uppercase">
                {fmtDateTime(draft.scheduled_for)}
              </div>
            </div>
          </div>
          <p className="text-center font-sans text-[10px] text-slate-400 mt-3 tracking-wider uppercase">
            Use the buttons under the picture to change the media
          </p>
        </div>

        {/* Editor panel */}
        <div className="space-y-6">
          {/* Admin sent-back feedback — shown when the admin disapproved this post */}
          {post.approval_status === 'rejected' && (
            <div className="border border-amber-300 bg-amber-50 px-4 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="font-sans text-[10px] tracking-[0.2em] uppercase font-semibold text-amber-700">
                  Sent back by admin — please revise & resend
                </span>
              </div>
              {post.admin_feedback ? (
                <div className="flex items-start gap-2 pl-0.5">
                  <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-amber-600 flex-shrink-0" />
                  <div>
                    <div className="font-sans text-[10px] tracking-wider uppercase text-amber-700/80">
                      Admin notes
                    </div>
                    <p className="font-sans text-sm text-amber-900 mt-0.5 whitespace-pre-line">
                      {post.admin_feedback}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="font-sans text-xs text-amber-800 pl-6">No notes were provided.</p>
              )}
            </div>
          )}

          {/* Section: schedule & format */}
          <section className="space-y-3">
            <SectionTitle icon={<Calendar className="w-3.5 h-3.5" />} title="Schedule & Format" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Day Label">
                <input
                  type="text"
                  value={draft.day_label || ''}
                  onChange={(e) => update({ day_label: e.target.value })}
                  className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400"
                  placeholder="Monday"
                />
              </Field>
              <Field label="Theme">
                <input
                  type="text"
                  value={draft.theme || ''}
                  onChange={(e) => update({ theme: e.target.value })}
                  className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400"
                  placeholder="Aspirational Opener"
                />
              </Field>
              <Field label="Post Type">
                <select
                  value={draft.post_type}
                  onChange={(e) => update({ post_type: e.target.value })}
                  className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A]"
                >
                  <option>Feed Post</option>
                  <option>Reel</option>
                  <option>Carousel</option>
                  <option>Story</option>
                </select>
              </Field>
              <Field label="Scheduled For">
                <input
                  type="datetime-local"
                  value={draft.scheduled_for ? toLocalInput(draft.scheduled_for) : ''}
                  onChange={(e) =>
                    update({ scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : null })
                  }
                  className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A]"
                />
              </Field>
            </div>
          </section>

          {/* Section: content */}
          <section className="space-y-3">
            <SectionTitle icon={<MessageCircle className="w-3.5 h-3.5" />} title="Caption & Copy" />
            <Field label="Caption">
              <textarea
                value={draft.caption || ''}
                onChange={(e) => update({ caption: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm h-40 resize-none leading-relaxed bg-white text-[#0D1B2A] placeholder:text-slate-400"
                placeholder="Write the caption…"
              />
            </Field>
            <Field label="Call to Action">
              <input
                type="text"
                value={draft.cta || ''}
                onChange={(e) => update({ cta: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400"
                placeholder="Tap the link in bio to plan your charter."
              />
            </Field>
            <Field label="Hashtags">
              <input
                type="text"
                value={(draft.hashtags || []).join(' ')}
                onChange={(e) => update({ hashtags: e.target.value.split(/\s+/).filter(Boolean) })}
                className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400"
                placeholder={activeBrand().hashtagPlaceholder}

              />
              {draft.hashtags && draft.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {draft.hashtags.map((h, i) => (
                    <span
                      key={`${h}-${i}`}
                      className="font-sans text-[11px] text-[#1A3B5C] bg-[#F3EFE6] border border-[#C9A961]/30 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                    >
                      <Hash className="w-2.5 h-2.5" />
                      {h.replace(/^#/, '')}
                    </span>
                  ))}
                </div>
              )}
            </Field>
          </section>

          {/* Section: AI image prompt */}
          <section className="space-y-3">
            <SectionTitle icon={<Wand2 className="w-3.5 h-3.5" />} title="AI Image Prompt" />
            <Field label="Prompt used when generating this post's image">
              <input
                type="text"
                value={draft.image_prompt || ''}
                onChange={(e) => update({ image_prompt: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2.5 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400"
                placeholder="Golden-hour drone shot of a yacht on turquoise water…"
              />
            </Field>
          </section>

          {err && (
            <div className="font-sans text-xs text-red-600 border border-red-200 bg-red-50 px-3 py-2">{err}</div>
          )}

          {/* Actions — full-width stacked on mobile for easy tapping */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={save}
              disabled={saving || !dirty}
              className={`font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-3 flex items-center justify-center gap-2 ${
                dirty && !saving
                  ? 'bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651]'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : savedFlash ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              {savedFlash ? 'Saved' : 'Save Changes'}
            </button>

            <button
              onClick={approve}
              disabled={approving}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-3 bg-[#0D1B2A] text-white hover:bg-[#1A3B5C] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send to Admin for Approval
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div className="flex items-center gap-2 border-b border-[#C9A961]/25 pb-2">
    <span className="text-[#C9A961]">{icon}</span>
    <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B6F47] font-semibold">{title}</span>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-1.5">{label}</div>
    {children}
  </div>
);

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}

export default PostEditor;
